import React from "react";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  BookOpen,
  Code,
  Cloud,
  Database,
  Layout,
  TrendingUp,
  Shield,
  Users,
  CheckCircle2,
} from "lucide-react";
import heroImage from "../../Assets/hero.png";
import bridgeImage from "../../Assets/bridge.png";
import codingImage from "../../Assets/coding.png";
import logo from "../../Assets/logo.png";
import Routes from "../../Routes/Routes";

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" as const } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
};

const Btn: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "outline";
  size?: "md" | "lg";
  className?: string;
}> = ({ children, onClick, variant = "primary", size = "md", className = "" }) => {
  const base =
    "inline-flex items-center justify-center font-semibold rounded-full transition-all duration-300 cursor-pointer border-0";
  const sizes = size === "lg" ? "h-14 px-8 text-lg" : "h-10 px-6 text-sm";
  const variants =
    variant === "primary"
      ? "bg-[#a855f7] text-white shadow-lg hover:bg-[#9333ea] hover:-translate-y-0.5"
      : "bg-white text-gray-800 border border-gray-200 hover:bg-gray-50";
  return (
    <button onClick={onClick} className={`${base} ${sizes} ${variants} ${className}`}>
      {children}
    </button>
  );
};

export default function LandingPage() {
  const history = useHistory();

  const goToSignin = () => history.push(Routes.signin);

  return (
    <div className="min-h-screen bg-white overflow-hidden" style={{ fontFamily: "system-ui, sans-serif" }}>
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Her Springboard" className="h-10 w-10 rounded-md object-contain" />
            <span className="font-bold text-xl tracking-tight text-gray-900">Her Springboard</span>
          </div>
          <div className="hidden md:flex items-center gap-8 font-medium text-sm text-gray-600">
            <a href="#about" className="hover:text-purple-600 transition-colors">Our Mission</a>
            <a href="#curriculum" className="hover:text-purple-600 transition-colors">Curriculum</a>
            <a href="#mentorship" className="hover:text-purple-600 transition-colors">Partners</a>
          </div>
          <Btn onClick={goToSignin} className="shadow-purple-200">
            Sign In
          </Btn>
        </div>
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 px-6">
          <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-purple-50 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-[600px] h-[600px] bg-pink-50 rounded-full blur-[100px] pointer-events-none" />

          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="relative z-10"
            >
              <motion.div
                variants={fadeInUp}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 text-purple-700 font-medium text-sm mb-6"
              >
                <Sparkles className="w-4 h-4" />
                <span>Empowering women in tech</span>
              </motion.div>
              <motion.h1
                variants={fadeInUp}
                className="text-5xl lg:text-7xl font-bold leading-[1.1] mb-6 text-gray-900"
              >
                Your launchpad to a{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500">
                  transformative
                </span>{" "}
                career.
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-lg lg:text-xl text-gray-500 mb-10 max-w-xl leading-relaxed">
                Careers are no longer linear. We bridge the gap between academic learning and real-world tech demands
                through industry-grade, customized training.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-4">
                <Btn size="lg" onClick={goToSignin}>
                  Start Your Journey <ArrowRight className="ml-2 w-5 h-5" />
                </Btn>
                <Btn size="lg" variant="outline" onClick={() => document.getElementById("curriculum")?.scrollIntoView({ behavior: "smooth" })}>
                  Explore Courses
                </Btn>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl border border-gray-100 relative group">
                <div className="absolute inset-0 bg-gradient-to-tr from-purple-400/20 to-transparent mix-blend-overlay z-10" />
                <img
                  src={heroImage}
                  alt="Women collaborating in tech"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="absolute -bottom-8 -left-8 bg-white p-6 rounded-2xl shadow-xl border border-gray-100 backdrop-blur-sm max-w-[280px]"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex -space-x-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-purple-50 flex items-center justify-center">
                        <Users className="w-4 h-4 text-purple-400" />
                      </div>
                    ))}
                  </div>
                  <div className="text-sm font-semibold">Join 500+ women</div>
                </div>
                <p className="text-xs text-gray-500">Who have successfully launched their tech careers with us.</p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* The Reality Gap Section */}
        <section id="about" className="py-24 bg-gray-50 relative">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid lg:grid-cols-2 gap-16 items-center"
            >
              <motion.div variants={fadeInUp}>
                <div className="aspect-square rounded-3xl overflow-hidden relative">
                  <img src={bridgeImage} alt="Abstract bridge representation" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent" />
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="space-y-8">
                <div>
                  <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                    The gap is real. <br />
                    <span className="text-gray-400">We build the bridge.</span>
                  </h2>
                  <p className="text-lg text-gray-500 leading-relaxed">
                    The distance between industry expectations and academic expertise is growing. In a world of rapidly
                    advancing technology, falling behind is not an option.
                  </p>
                </div>

                <div className="space-y-6">
                  {[
                    "Careers are multi-faceted and non-linear",
                    "Curriculum must address current AND futuristic needs",
                    "We stimulate creativity to develop passionate coders",
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-purple-50 flex flex-shrink-0 items-center justify-center mt-1">
                        <CheckCircle2 className="w-5 h-5 text-purple-600" />
                      </div>
                      <p className="text-lg font-medium text-gray-800">{text}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Pathway Section */}
        <section className="py-24 relative overflow-hidden bg-gray-900">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-500/20 rounded-full blur-[100px]" />

          <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-5xl font-bold mb-16 text-white"
            >
              The Her Springboard Pathway
            </motion.h2>

            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 max-w-4xl mx-auto">
              {[
                { title: "Knowledge", desc: "Foundation" },
                { title: "Competence", desc: "Execution" },
                { title: "Quality", desc: "Excellence" },
                { title: "Success", desc: "Career" },
              ].map((step, i, arr) => (
                <React.Fragment key={i}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex flex-col items-center p-6 rounded-2xl bg-white/5 border border-white/10 w-full md:w-48"
                  >
                    <div className="text-2xl font-bold text-white mb-2">{step.title}</div>
                    <div className="text-purple-400 font-medium tracking-wide uppercase text-xs">{step.desc}</div>
                  </motion.div>
                  {i < arr.length - 1 && (
                    <ArrowRight className="hidden md:block w-8 h-8 text-white/20 flex-shrink-0" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* Curriculum */}
        <section id="curriculum" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">Master the skills that matter</h2>
              <p className="text-lg text-gray-500">
                Personalized learning paths tailored to your goals, guided by industry veterans.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { icon: Code, title: "Full-Stack Web Dev", desc: "Build robust, scalable web applications from front to back." },
                { icon: Cloud, title: "Cloud & DevOps", desc: "Master modern deployment, scaling, and cloud architecture." },
                { icon: Database, title: "Data Science & AI", desc: "Harness the power of data and build intelligent systems." },
                { icon: Layout, title: "UI/UX Design", desc: "Craft intuitive, beautiful, and user-centered experiences." },
                { icon: TrendingUp, title: "Digital Marketing", desc: "Drive growth through data-driven marketing strategies." },
                { icon: Shield, title: "Cybersecurity", desc: "Protect systems and data in an increasingly complex world." },
              ].map((course, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="group p-8 rounded-3xl bg-white border border-gray-100 hover:border-purple-200 hover:shadow-xl hover:shadow-purple-50 transition-all duration-300 cursor-pointer"
                  onClick={goToSignin}
                >
                  <div className="w-14 h-14 rounded-2xl bg-purple-50 flex items-center justify-center mb-6 group-hover:bg-purple-600 transition-colors duration-300">
                    <course.icon className="w-7 h-7 text-purple-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900">{course.title}</h3>
                  <p className="text-gray-500 leading-relaxed mb-6">{course.desc}</p>
                  <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-2 transition-transform duration-300">
                    Explore Syllabus <ArrowRight className="ml-1 w-4 h-4" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Partners / Mentorship */}
        <section id="mentorship" className="py-24 bg-gray-50 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="space-y-8"
            >
              <motion.div variants={fadeInUp}>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">Powered by industry leaders.</h2>
                <p className="text-lg text-gray-500 leading-relaxed">
                  We don't just teach theory. Her Springboard partners with <strong>NimbusGurus</strong> to bring you
                  deep industry insights and curriculum designed for the future.
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex gap-6 pt-4">
                <div className="flex-1 p-6 rounded-2xl bg-white border border-gray-100 shadow-sm">
                  <div className="font-bold text-xl mb-2 text-gray-900">NimbusGurus</div>
                  <p className="text-sm text-gray-500">
                    Strategic technology consulting and enterprise architecture, bringing industry-grade expertise to
                    every learner.
                  </p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-[2rem] overflow-hidden">
                <img src={codingImage} alt="Focused woman coding" className="w-full h-full object-cover" />
              </div>
              <div className="absolute top-1/2 -left-12 -translate-y-1/2 bg-white p-6 rounded-2xl shadow-xl border border-gray-100 max-w-[240px] hidden md:block">
                <BookOpen className="w-8 h-8 text-purple-600 mb-4" />
                <div className="font-bold text-lg mb-2 text-gray-900">Industry Veteran Trainers</div>
                <p className="text-sm text-gray-400">
                  Learn directly from professionals who build products you use every day.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-32 relative">
          <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-5xl md:text-7xl font-bold mb-8 text-gray-900"
            >
              Ready to take the leap?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-500 mb-12 max-w-2xl mx-auto"
            >
              Join a community of ambitious women transforming their potential into excellence. Your future in tech
              starts here.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Btn size="lg" onClick={goToSignin} className="h-16 px-10 text-xl shadow-2xl shadow-purple-200 hover:scale-105">
                Apply for the Next Cohort
              </Btn>
            </motion.div>
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-white py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Her Springboard" className="h-10 w-10 rounded-md object-contain" />
            <span className="font-bold text-xl tracking-tight">Her Springboard</span>
          </div>
          <div className="text-sm text-white/50 text-center md:text-left">
            © {new Date().getFullYear()} Her Springboard. In partnership with NimbusGurus.
          </div>
        </div>
      </footer>
    </div>
  );
}
